import { rgbAt } from '../image/analysis.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp01, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import {
  clampRect,
  computeSkinReference,
  heuristicOptions,
  insetRect,
  isHairLike,
  isSkinLike,
  localDetailAt,
  pixelBounds,
  pixelFeatures,
  type HairHeuristicOptions,
  type SkinReference,
} from './hairOcclusion.js';

/**
 * Headwear analysis for the «Головной убор» check.
 *
 * The band between the estimated top of the head and the forehead is where a
 * cap, hat or hood sits. Inside that band the check looks for a *large, smooth,
 * low-texture region that is neither hair nor skin*, compared against the skin
 * measured lower on the face, plus an unnaturally flat hairline silhouette.
 *
 * This is a detector of visual symptoms, not a classifier: it never says what
 * the object is, and the profile decides what a finding means. The core never
 * knows whether headwear is allowed for a document — the profile may mark it as
 * allowed for religious or medical reasons, and then the check can only pass or
 * warn.
 */

export interface HeadwearHeuristicOverrides {
  darkFactor?: number;
  saturationFactor?: number;
  detailFactor?: number;
}

export interface HeadwearAnalysisInput {
  pixels: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
  overrides?: HeadwearHeuristicOverrides;
}

export interface HeadwearAnalysis {
  /** True when a large non-hair, non-skin region was found above the forehead. */
  detected: boolean;
  /** 0..1, how sure the heuristics are about `detected`. */
  confidence: number;
  /** Share of the band classified as neither hair nor skin, 0..1. */
  coverage: number;
  /** True when the suspicious pixels are as smooth as a fabric/plastic surface. */
  smooth: boolean;
  /** 1 = perfectly straight hairline silhouette, 0 = strongly curved. */
  flatness: number;
  /** Spread of the detected hairline, in image-height units. */
  hairlineSpread: number;
  bandRect: NormalizedRect;
  bandPixels: number;
  foreignPixels: number;
  bandTooSmall: boolean;
  mode: 'heuristic' | 'segmentation';
  notes: string[];
}

/** 0..1 stddev of the hairline height across the band, as a flatness score. */
const measureFlatness = (
  pixels: AnalysisBuffer,
  band: NormalizedRect,
  options: HairHeuristicOptions,
  faceHeight: number,
): { flatness: number; spread: number; columns: number } => {
  const { data, width, height, channels } = pixels;
  const { x0, y0, x1, y1 } = pixelBounds(band, width, height);
  const columns = Math.max(1, Math.min(16, x1 - x0));
  const step = Math.max(1, Math.floor((x1 - x0) / columns));
  const yLimit = Math.min(height, y1 + Math.max(3, Math.round((y1 - y0) * 2)));
  const profile: number[] = [];

  for (let x = x0; x < x1; x += step) {
    for (let y = y0; y < yLimit; y += 1) {
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      if (isSkinLike(features, options)) {
        profile.push((y + 0.5) / height);
        break;
      }
    }
  }

  if (profile.length < 3) {
    return { flatness: 0.5, spread: 0, columns: profile.length };
  }
  const mean = profile.reduce((sum, value) => sum + value, 0) / profile.length;
  const variance =
    profile.reduce((sum, value) => sum + (value - mean) ** 2, 0) / profile.length;
  const spread = Math.sqrt(variance);
  const reference = Math.max(faceHeight * 0.25, 1e-3);
  return { flatness: clamp01(1 - spread / reference), spread, columns: profile.length };
};

export const analyseHeadwear = (input: HeadwearAnalysisInput): HeadwearAnalysis => {
  const { pixels, geometry } = input;
  const { data, width, height, channels, luma } = pixels;
  const notes: string[] = [];

  const headTopY = geometry.headTopPoint.y;
  const foreheadY = geometry.foreheadPoint.y;
  const bandHeight = foreheadY - headTopY;
  const bandRect = clampRect({
    x: geometry.headBox.x,
    y: headTopY,
    width: geometry.headBox.width,
    height: Math.max(bandHeight, 0.01),
  });

  const skinReference: SkinReference = computeSkinReference(
    pixels,
    insetRect(geometry.faceBox, 0.18),
  );
  const options = heuristicOptions(skinReference, input.overrides);

  const { x0, y0, x1, y1 } = pixelBounds(bandRect, width, height);
  const bandPixelsAvailable = (x1 - x0) * (y1 - y0);
  const bandTooSmall =
    geometry.headTopSource === 'unavailable' || bandHeight <= 0 || bandPixelsAvailable < 16;
  if (bandTooSmall) {
    notes.push('Не удалось надёжно оценить область между макушкой и лбом.');
  }

  let bandPixels = 0;
  let foreignPixels = 0;
  let foreignDetailSum = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      const detail = localDetailAt(luma, width, height, x, y);
      bandPixels += 1;
      if (!isHairLike(features, detail, options) && !isSkinLike(features, options)) {
        foreignPixels += 1;
        foreignDetailSum += detail;
      }
    }
  }

  const coverage = bandPixels > 0 ? foreignPixels / bandPixels : 0;
  const foreignDetail = foreignPixels > 0 ? foreignDetailSum / foreignPixels : 0;
  const smooth = foreignPixels === 0 || foreignDetail <= Math.max(skinReference.detail * 1.5 + 2, 6);
  const { flatness, spread, columns } = measureFlatness(pixels, bandRect, options, geometry.faceHeight);

  const adequacy = clamp01(bandPixels / 64);
  const confidence = clamp01(
    0.3 * clamp01(coverage / 0.6) + 0.3 * flatness + 0.25 * (smooth ? 1 : 0) + 0.15 * adequacy,
  );
  const detected = !bandTooSmall && coverage >= 0.45 && smooth;

  if (columns < 3 && !bandTooSmall) {
    notes.push('Силуэт линии роста волос измерен по малому числу колонок.');
  }

  return {
    detected,
    confidence: roundTo(confidence, 3),
    coverage: roundTo(coverage, 4),
    smooth,
    flatness: roundTo(flatness, 3),
    hairlineSpread: roundTo(spread, 4),
    bandRect,
    bandPixels,
    foreignPixels,
    bandTooSmall,
    mode: 'heuristic',
    notes,
  };
};
